// src/firebase.js
import { initializeApp } from "firebase/app";
import { getFirestore, collection, addDoc, getDocs, updateDoc, deleteDoc, doc, query, where } from "firebase/firestore";
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut } from "firebase/auth";

// 🔹 Firebase 설정 (Firebase Console에서 복사한 값 입력)
const firebaseConfig = {
  apiKey: "AIzaSyC2HjAng6y8bv_8jUPd6d1JWQd0X0qvsis",
  authDomain: "communitech-98ffe.firebaseapp.com",
  projectId: "communitech-98ffe",
  storageBucket: "communitech-98ffe.firebasestorage.app",
  messagingSenderId: "424805002601",
  appId: "1:424805002601:web:a4a72e541af37d4ebae1f8",
  measurementId: "G-S5GQ7KEMGV"
};


// 🔹 Firebase 앱 초기화
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const provider = new GoogleAuthProvider();
const db = getFirestore(app);


// 🔹 Firestore CRUD 함수
const addData = async (data) => {
  try {
    await addDoc(collection(db, "byFaith"), data);
    // console.log("데이터 추가 완료!");
    return true;
  } catch (error) {
    console.error("데이터 추가 오류:", error);
    return false;
  }
};

const getData = async (userId) => {
  try {
    const q = query(collection(db, "byFaith"), where("userId", "==", userId));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("데이터 가져오기 오류:", error);
  }
};

const updateData = async (id, newData) => {
  try {
    const docRef = doc(db, "byFaith", id);
    await updateDoc(docRef, newData);
    // console.log("데이터 업데이트 완료!");
  } catch (error) {
    console.error("데이터 업데이트 오류:", error);
  }
};
const updateDataByUserId = async (userId, newData) => {
  try {
    if (!auth.currentUser) throw new Error("로그인이 필요합니다.");

    const q = query(collection(db, "byFaith"), where("userId", "==", userId)); // 🔥 userId로 데이터 찾기
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      throw new Error("해당 userId의 데이터를 찾을 수 없습니다.");
    }

    const docRef = doc(db, "byFaith", querySnapshot.docs[0].id); // 첫 번째 문서 참조
    await updateDoc(docRef, newData);

    // console.log("데이터 업데이트 완료!");
  } catch (error) {
    console.error("데이터 업데이트 오류:", error);
  }
};
const updateCheckListWithInputs = async (userId, dayIndex, inputs) => {
  try {
    if (!auth.currentUser) throw new Error("로그인이 필요합니다.");

    // 🔹 Firestore에서 userId 문서 찾기
    const q = query(collection(db, "byFaith"), where("userId", "==", userId));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      throw new Error("해당 userId의 데이터를 찾을 수 없습니다.");
    }

    const docRef = doc(db, "byFaith", querySnapshot.docs[0].id);
    const docData = querySnapshot.docs[0].data();

    // 🔹 checkList가 없거나 undefined면 빈 객체로 초기화
    let updatedCheckList = docData.checkList && typeof docData.checkList === "object"
      ? { ...docData.checkList }
      : {};

    // 🔥 `dayIndex`를 "day1", "day2", "day3" 형식으로 변환
    const dayKey = `Day.${dayIndex + 1}`;

    // 🔥 checkList 내부가 배열이 아닌 경우 초기화
    if (!updatedCheckList[dayKey] || !Array.isArray(updatedCheckList[dayKey])) {
      updatedCheckList[dayKey] = [];
    }

    // 🔥 `inputs` 값을 Firestore에서 지원하는 객체(Map) 구조로 변환하여 저장
    updatedCheckList[dayKey] = inputs.map((value) => (value !== undefined ? value : null));

    // 🔹 Firestore에 업데이트 (undefined 값 제거)
    const cleanData = JSON.parse(JSON.stringify({ checkList: updatedCheckList, lastestUpdateDate: getKoreanTime() }));

    await updateDataByUserId(userId, cleanData);

    // console.log(`checkList[${dayKey}] 업데이트 완료!`);
  } catch (error) {
    console.error("checkList 업데이트 오류:", error);
  }
};


const getFilteredCheckList = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, "byFaith")); // ✅ Firestore users 컬렉션 가져오기
    const filteredData = [];
    let cnt = 0;
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const checkList = data.checkList || {}; // ✅ checkList 가져오기 (없으면 빈 객체)
      cnt++;
      
      if (
        Array.isArray(checkList["Day.1"]) && checkList["Day.1"].length !== 0
      ) {
        filteredData.push(data.checkList); // ✅ 해당 문서 저장
      }
    });

    // console.log("✅ 필터링된 데이터:", filteredData);
    // console.log("✅ 필터링된 데이터:", cnt);
    return filteredData;
  } catch (error) {
    console.error("🔥 Firestore 오류:", error);
    return [];
  }
};


getFilteredCheckList()



const deleteData = async (id) => {
  try {
    const docRef = doc(db, "byFaith", id);
    await deleteDoc(docRef);
    // console.log("데이터 삭제 완료!");
  } catch (error) {
    console.error("데이터 삭제 오류:", error);
  }
};

// 🔹 Firebase 인증 관련 함수
const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, provider);
    // console.log("User Info:", result.user);
  } catch (error) {
    console.error("Google 로그인 오류:", error);
  }
};

const logout = async () => {
  try {
    await signOut(auth);
    // console.log("로그아웃 완료");
  } catch (error) {
    console.error("로그아웃 오류:", error);
  }
};


const getKoreanTime = () => {
  const now = new Date();

  // 🔹 한국 시간(KST) 변환
  const options = { timeZone: "Asia/Seoul", hour12: false };

  const formatter = new Intl.DateTimeFormat("ko-KR", {
    ...options,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  const parts = formatter.formatToParts(now);
  const year = parts.find((p) => p.type === "year").value;
  const month = parts.find((p) => p.type === "month").value;
  const day = parts.find((p) => p.type === "day").value;
  const hour = parts.find((p) => p.type === "hour").value.padStart(2, "0");
  const minute = parts.find((p) => p.type === "minute").value.padStart(2, "0");
  const second = parts.find((p) => p.type === "second").value.padStart(2, "0");

  return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
};


export { db, auth, signInWithGoogle, logout, addData, getData, updateData, updateDataByUserId, updateCheckListWithInputs, deleteData };
